import "./campaign-trigger-selector.css";
import {Button, DatePicker, Tooltip} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {useEffect, useRef, useState} from "react";
import ButtonGroup from "../forms/buttonGroup/ButtonGroup";
import ClickIcon from "../../assets/icons/ClickIcon";
import {TimerIcon} from "../../assets/icons/TimerIcon";
import {useSteps} from "../../hooks/useSteps";
import TimeIllustration from "../../assets/illustrations/time-purple.png";
import dayjs, {Dayjs} from "dayjs";
import {updateCampaignTriggers} from "../../api/updateCampaignTriggers";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import useAppContext from "../../hooks/useAppContext";
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
dayjs.extend(localeData);
dayjs.extend(weekday);

interface CampaignSettingsProps {
    onSubmitSettings: () => void;
    onCancel: () => void;
    campaignId: number;
}

const CampaignTriggerSelector = ({ onSubmitSettings, campaignId, onCancel }: CampaignSettingsProps) => {
    const { selectedDomain } = useUserContext();
    const { data: { campaigns }, actions: { loadCampaigns }} = useAppContext();
    const [selectedTriggerId, setSelectedTriggerId] = useState<null | "manual" | "timed">(null);
    const [timedValue, setTimedValue] = useState<Dayjs | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const isDirty = useRef(false);
    const { goBack, goNext } = useSteps();
    const { showToast } = useToast();

    const onUpdateTriggers = async () => {
        if(isDirty.current && selectedDomain && selectedTriggerId) {
            setIsLoading(true);
            const formattedTime = timedValue ? new Date(timedValue.format('YYYY-MM-DD HH:mm:ss')) : undefined;
            const response = await updateCampaignTriggers(selectedDomain, campaignId, selectedTriggerId, formattedTime);
            if(response?.data?.state === 'success') {
                loadCampaigns();
                onSubmitSettings();
                goNext();
            } else {
                showToast({ skin: ToastSkin.error, text: 'Failed to update a trigger for your campaign' });
            }
            setIsLoading(false);
        } else if(!isDirty.current) {
            onSubmitSettings();
            goNext();
        }
    }

    const buttonGroupItems = [{
        id: 'manual',
        title: 'Send Manually',
        description: 'Launch the campaign whenever you’re ready',
        icon: <ClickIcon width={24} color="#1575fb" />,
        isSelected: selectedTriggerId === 'manual',
    }, {
        id: 'timed',
        title: 'Schedule for Later',
        description: 'Pick a date and time to send it automatically.',
        icon: <TimerIcon width={24} />,
        isSelected: selectedTriggerId === 'timed',
    }];

    const isTimed = selectedTriggerId === 'timed';
    const isManual = selectedTriggerId === 'manual';
    const isDisabled = (isTimed && !timedValue) || (!isManual && !isTimed);

    const disablePastDates = (current: Dayjs | null): boolean => {
        return current !== null && current < dayjs().startOf('day');
    };

    const disablePastTimes = (date: Dayjs | null) => {
        if (!date) return {};

        const now = dayjs();
        if (date.isSame(now, 'day')) {
            const currentHour = now.hour();
            const currentMinute = now.minute();

            return {
                // @ts-ignore
                disabledHours: (): number[] => [...Array(currentHour).keys()],
                disabledMinutes: (selectedHour: number): number[] => {
                    // @ts-ignore
                    return selectedHour === currentHour ? [...Array(currentMinute).keys()] : [];
                },
            };
        }
        return {};
    };

    const onTriggerTypeClick = (newId: "manual" | "timed") => {
        isDirty.current = true;
        setSelectedTriggerId(newId);
    }

    useEffect(() => {
        if(selectedTriggerId === "manual") {
            setTimedValue(undefined);
        }
    }, [selectedTriggerId]);

    useEffect(() => {
        if(campaignId) {
            const campaign = campaigns?.data?.find((campaign: any) => campaign.id === campaignId);
            const campaignTriggerType = campaign?.triggerType;
            const campaignTriggerTime = campaign?.triggerTime;
            if(campaignTriggerType) {
                setSelectedTriggerId(campaignTriggerType);
            }
            if(campaignTriggerTime) {
                console.log("triggerTime", campaignTriggerTime);
                console.log("parsed", dayjs(campaignTriggerTime));
                console.log("isValid", dayjs(campaignTriggerTime).isValid());
                setTimedValue(dayjs(campaignTriggerTime));
            }

        }
    }, [campaignId])


    return (
        <div className="campaign-settings-container">
            <div className="left">
                <div>
                    <div className="title">When should the campaign be sent?</div>
                    <div className="campaign-trigger-selector field-container">
                        <ButtonGroup disabled={isLoading} descriptionAlwaysOn={true} items={buttonGroupItems} onClick={(newId) => onTriggerTypeClick(newId as "manual" | "timed")} />
                    </div>
                    <div className="field-container">
                        <Tooltip title={isTimed ? "" : "To pick a specific time, please select Schedule For Later option above"}>
                            <DatePicker
                                // @ts-ignore
                                disabledDate={disablePastDates}
                                // @ts-ignore
                                disabledTime={disablePastTimes}
                                disabled={!isTimed || isLoading}
                                showTime
                                className="trigger-time-picker"
                                value={timedValue}
                                onChange={(newValue) => {
                                    isDirty.current = true;
                                    setTimedValue(newValue)
                                }}
                            />
                        </Tooltip>
                    </div>
                </div>
                <div className="bottom">
                    <div className="actions">
                        <Button type="default" size="large" className="back-step-button" onClick={goBack} icon={<LeftOutlined />}>{'Back'}</Button>
                        <Button loading={isLoading} disabled={isDisabled} type="primary" size="large" className="next-step-button" onClick={onUpdateTriggers} iconPosition="end" icon={<RightOutlined />}>Update Trigger</Button>
                    </div>
                </div>
            </div>
            <div className="right">
                <div className="illustration">
                    <img src={TimeIllustration} />
                </div>
            </div>
        </div>
    );
}

export default CampaignTriggerSelector;